import { useCallback, useEffect, useMemo, useRef, useState, ReactElement } from 'react';
import { useParams } from 'react-router-dom';
import { Box, Card, CardContent, CardActions, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { PageTitle, ProgressButton, ControlledCodeEditor, CodeEditor } from '@localstack/ui';
import { useForm } from 'react-hook-form';
import { DEFAULT_DYNAMODB_ROUTES } from '@localstack/constants';

import {
  useRoutes,
  useAwsEffect,
  useAwsGetter,
  useSnackbar,
  extractDynamoDBPrimaryKey,
  buildDynamoDBTypedPrimaryKey,
  dynamoDBTypedToPlain,
  dynamoDBPlainToTyped,
  VALIDATION_RULES,
} from '@localstack/services';

import type { DynamoDBTypedItem } from '@localstack/services';

import { TableNavTabs } from './components';
import { DynamoDBProps } from './types';

const getFieldSampleValue = (type: Optional<string>) => {
  if (type === 'N') return 123;
  if (type === 'BOOL') return true;

  return 'hello-world';
};

interface JsonItemInput {
  json: string;
}

export const DynamoDBTableItemUpsert = ({
  Layout,
  clientOverrides,
  routes = DEFAULT_DYNAMODB_ROUTES,
}: DynamoDBProps): ReactElement => {
  const { goto } = useRoutes();
  const { showSnackbar } = useSnackbar();
  const { tableName, hashKey, rangeKey } = useParams<'tableName' | 'hashKey' | 'rangeKey'>();

  const [placeholder, setPlaceholder] = useState('');
  const [viewMode, setViewMode] = useState<'plain' | 'typed'>('plain');
  // read-only typed (DynamoDB JSON) rendering of the current edits, for inspection only
  const [typedPreview, setTypedPreview] = useState('');

  // the originally-loaded typed item; used as the merge reference on save so attributes the
  // user did not change keep their exact types (B/SS/NS/BS) instead of being re-marshalled
  const typedSnapshot = useRef<Optional<DynamoDBTypedItem>>(undefined);
  // guards the initial load effect so revalidation never clobbers in-progress edits
  const initializedItemKey = useRef<Optional<string>>(undefined);

  const { data: table } = useAwsGetter('DynamoDB', 'describeTable', [{ TableName: tableName }], { clientOverrides });

  const itemPrimaryKey = useMemo(() => {
    if (!hashKey || !table) return undefined;
    return buildDynamoDBTypedPrimaryKey(table?.Table, hashKey, rangeKey);
  }, [hashKey, rangeKey, table]);

  const { data: item } = useAwsGetter('DynamoDB', 'getItem', [{ TableName: tableName, Key: itemPrimaryKey }], {
    clientOverrides,
  });

  const { putItem, isLoading } = useAwsEffect('DynamoDB', ['putItem'], {
    revalidate: ['scan', 'query', 'getItem'],
    clientOverrides,
  });

  const { control, handleSubmit, setValue, getValues } = useForm<JsonItemInput>({ mode: 'all' });

  const isEditing = !!hashKey;

  const handleUpsert = useCallback(
    async (jsonItem: JsonItemInput) => {
      // the plain view is the single source of truth; merge against the snapshot so untouched
      // set/binary attributes keep their exact types instead of being re-marshalled
      const Item = dynamoDBPlainToTyped(JSON.parse(jsonItem.json), typedSnapshot.current);

      await putItem({ TableName: tableName as string, Item });
      if (isEditing) {
        showSnackbar({ message: 'Item saved successfully', severity: 'success' });
      } else {
        goto(routes.RESOURCES_DYNAMODB_TABLE_ITEMS, { tableName });
      }
    },
    [isEditing],
  );

  const handleViewModeChange = useCallback(
    (_event: React.MouseEvent<HTMLElement>, nextMode: 'plain' | 'typed' | null) => {
      if (!nextMode || nextMode === viewMode) return;

      // typed view is read-only: derive it from the current plain edits for inspection
      if (nextMode === 'typed') {
        try {
          const typed = dynamoDBPlainToTyped(JSON.parse(getValues('json') || '{}'), typedSnapshot.current);
          setTypedPreview(JSON.stringify(typed, null, 2));
        } catch {
          showSnackbar({ message: 'Fix the JSON before viewing the DynamoDB JSON form', severity: 'error' });
          return;
        }
      }

      setViewMode(nextMode);
    },
    [viewMode],
  );

  // set a sample json value for the (plain) editor
  useEffect(() => {
    if (!table) return;

    const primaryKey = extractDynamoDBPrimaryKey(table?.Table);
    const primaryKeyExample = buildDynamoDBTypedPrimaryKey(
      table?.Table,
      getFieldSampleValue(primaryKey.hashKeyDef?.AttributeType),
      getFieldSampleValue(primaryKey.rangeKeyDef?.AttributeType),
    );

    setPlaceholder(JSON.stringify(dynamoDBTypedToPlain(primaryKeyExample), null, 2));
  }, [table]);

  // initialize the editor once per loaded item; guard against revalidation clobbering edits
  useEffect(() => {
    if (!item?.Item) return;
    const itemKey = `${tableName}/${hashKey ?? ''}/${rangeKey ?? ''}`;
    if (initializedItemKey.current === itemKey) return;

    initializedItemKey.current = itemKey;
    typedSnapshot.current = item.Item as DynamoDBTypedItem;
    setValue('json', JSON.stringify(dynamoDBTypedToPlain(item.Item as DynamoDBTypedItem), null, 2));
  }, [item, tableName, hashKey, rangeKey]);

  return (
    <Layout
      title={
        <PageTitle
          title={isEditing ? 'Edit Item' : 'Create Item'}
          breadcrumbs={[
            ['DynamoDB', () => goto(routes.RESOURCES_DYNAMODB)],
            [tableName, () => goto(routes.RESOURCES_DYNAMODB_TABLE, { tableName })],
            ['Items', () => goto(routes.RESOURCES_DYNAMODB_TABLE_ITEMS, { tableName })],
            [isEditing ? 'Edit' : 'Create Item', null],
          ]}
        />
      }
      tabs={tableName && <TableNavTabs tableName={tableName} routes={routes} />}
      actions={
        <ProgressButton type="submit" form="update" variant="outlined" color="primary" loading={isLoading}>
          Submit
        </ProgressButton>
      }
    >
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="flex-end" mb={1}>
            <ToggleButtonGroup
              size="small"
              exclusive
              value={viewMode}
              onChange={handleViewModeChange}
              aria-label="DynamoDB item view"
            >
              <ToggleButton value="plain" aria-label="Editable JSON view">
                JSON
              </ToggleButton>
              <ToggleButton value="typed" aria-label="Read-only DynamoDB typed JSON view">
                DynamoDB JSON (read-only)
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
          {/* plain editor is the single source of truth; kept mounted so Submit always saves edits */}
          <form id="update" onSubmit={handleSubmit(handleUpsert)}>
            <Box display={viewMode === 'plain' ? 'block' : 'none'}>
              <ControlledCodeEditor
                language="json"
                control={control}
                name="json"
                rules={VALIDATION_RULES.validJson}
                placeholder={placeholder}
              />
            </Box>
          </form>
          {/* read-only twin of the editor: same component/styling, just not editable */}
          {viewMode === 'typed' && (
            <CodeEditor language="json" value={typedPreview} setValue={() => undefined} readOnly />
          )}
        </CardContent>
        <CardActions>
          <ProgressButton type="submit" form="update" variant="contained" color="primary" loading={isLoading}>
            Submit
          </ProgressButton>
        </CardActions>
      </Card>
    </Layout>
  );
};
